import http from 'apis/config/http-service';
import { useMutationWrapper } from 'apis/config/use-api-wrapper';
import type { AxiosError } from 'axios';
import { useCustomToast } from 'design-system';
import type { UseMutationOptions, UseMutationResult } from 'react-query';
import { InvoiceGenerationResult, RegenerateInvoicePayload } from './types';

const getEndpoint = (invoiceId: number) => `api/v1/cms/reconciliation/invoices/${invoiceId}/regenerate`;

type TData = InvoiceGenerationResult;
type TError = AxiosError;
type TVariables = RegenerateInvoicePayload;
type Options = Omit<UseMutationOptions<TData, TError, TVariables, unknown>, 'mutationFn'>;
type ReturnType = UseMutationResult<TData, TError, TVariables>;

/** Re-renders an invoice PDF keeping the same invoice number — a correction tool, never a workflow
 * step. 409 when paid or month closed; 400 with a reason ("Cannot regenerate: …"). */
export function useRegenerateInvoiceApi(options?: Options): ReturnType {
  const toast = useCustomToast();

  const mutationFn = async ({ invoiceId }: TVariables) => {
    try {
      const { data } = await http.post(getEndpoint(invoiceId));
      return data?.data ?? data;
    } catch (error: any) {
      if (!error.response?.status || !error.response?.data?.message) throw error;

      toast({
        description: error.response.data.message,
        status: 'error',
      });

      throw error;
    }
  };

  return useMutationWrapper(mutationFn, { ...options, mutationKey: ['reconciliation-regenerate-invoice'] });
}
